/**
 * Money and date rendering. Every rule here exists because breaking it is a specific,
 * observed defect on a billing screen.
 */
import type { ReactElement } from "react";

/**
 * Format a Stripe MINOR-UNITS amount. Amounts are never hard-coded in a component and never
 * sent to the server — the server maps {plan, interval, addons} to fixed Price IDs, so the
 * client supplying a price would be the client deciding what someone is charged.
 */
export function money(amountMinor: number, currency: string, locale?: string): string {
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency: currency.toUpperCase(),
  }).format(amountMinor / 100);
}

/**
 * Catalog amounts, in integer USD CENTS — the pricing package's unit since 2026-09-02.
 *
 * It used to take whole dollars and drop the decimals when the amount was round. Both had to go:
 * whole dollars could not express the $8.99 / $7.99 lineup this replaced, and hiding the decimals
 * renders $40.80 as "$40.8" the moment a total ends in a zero — which the current annual totals
 * ($40.80, $30.60) both do. Always two decimals, always cents in. The unit does not go back to
 * dollars just because today's monthly amounts ($4.00, $3.00) happen to be round.
 */
export function catalogMoney(amountCents: number, locale?: string): string {
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amountCents / 100);
}

/**
 * A date, in the browser's locale, wrapped in <time datetime> with the absolute UTC value
 * in the title. A bare localised date with no machine-readable original is unreadable to a
 * screen reader and ambiguous across timezones.
 */
export function DateText({ iso, locale }: { iso: string; locale?: string }): ReactElement {
  const d = new Date(iso);
  const readable = new Intl.DateTimeFormat(locale, {
    day: "numeric",
    month: "short",
    year: "numeric",
  }).format(d);
  return (
    <time dateTime={iso} title={d.toUTCString()}>
      {readable}
    </time>
  );
}

/** "2 hrs ago" / "Never used". `null` is a FACT here, not a missing value. */
export function relativeTime(iso: string | null | undefined, now = new Date()): string {
  if (!iso) return "Never used";
  const then = new Date(iso).getTime();
  const mins = Math.round((now.getTime() - then) / 60_000);
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs} hr${hrs === 1 ? "" : "s"} ago`;
  const days = Math.round(hrs / 24);
  if (days < 30) return `${days} day${days === 1 ? "" : "s"} ago`;
  return new Intl.DateTimeFormat(undefined, { day: "numeric", month: "short" }).format(then);
}

/**
 * A value that may be unknown.
 *
 * A real 0 and an unknown are DIFFERENT, and collapsing them tells a customer they have
 * used nothing. Unknown is an em dash, never a zero.
 */
export function Stat({ value }: { value: number | null | undefined }): ReactElement {
  if (value === null || value === undefined) return <span aria-label="Unavailable">&mdash;</span>;
  return <span className="pg-num">{new Intl.NumberFormat().format(value)}</span>;
}
